import { useState, useEffect } from "react";
import Axios from "utils/Axios";
import { Link, useParams, useNavigate } from "react-router-dom";
import InputAdornment from "@mui/material/InputAdornment";
import Icon from "@mui/material/Icon";
import Card from "@mui/material/Card";
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import MKInput from "components/MKInput";
import MKButton from "components/MKButton";
import CoverLayout from "./CoverLayout";
import bgImage from "assets/images/city-profile.jpg";
import CircularProgress from "@mui/material/CircularProgress";
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

function ResetPassword() {
  const { resetKey } = useParams();
  const navigate = useNavigate();
  
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordView, setPasswordView] = useState(false);
  const [confirmPasswordView, setConfirmPasswordView] = useState(false);
  const [hasPasswordInput, setHasPasswordInput] = useState(false);
  const [hasConfirmPasswordInput, setHasConfirmPasswordInput] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState("");
  const [keyValid, setKeyValid] = useState(false);
  const [success, setSuccess] = useState(false);

  // Verify reset key on component mount
  useEffect(() => {
    const verifyKey = async () => {
      setVerifying(true);
      try {
        const data = JSON.stringify({
          action: "verify_key",
          key: resetKey,
        });

        const res = await Axios.post("/?type=forgot_password", data, {
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (res.data.valid) {
          setKeyValid(true);
        } else {
          setApiError("This password reset link is invalid or has expired.");
        }
      } catch (err) {
        setApiError(
          "This password reset link is invalid or has expired. Please request a new one."
        );
      } finally {
        setVerifying(false);
      }
    };

    if (resetKey) {
      verifyKey();
    } else {
      setVerifying(false);
      setApiError("Invalid reset link. Please request a new one.");
    }
  }, [resetKey]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!password) {
      newErrors.password = "Password is required";
    } else if (password.length < 8) {
      newErrors.password = "Password must be at least 8 characters";
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = "Please confirm your password";
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    const data = JSON.stringify({
      action: "reset_password",
      key: resetKey,
      password: password,
    });

    setLoading(true);
    setApiError("");

    try {
      let res = await Axios.post("/?type=forgot_password", data, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (res.status === 200) {
        setSuccess(true);
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate("/sign-in/");
        }, 3000);
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.error ||
        "An unexpected error occurred. Please try again.";
      setApiError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Handle input for password fields
  const handlePasswordInput = (e) => {
    const inputValue = e.target.value;
    setPassword(inputValue);
    setHasPasswordInput(inputValue.length > 0);
  };

  const handleConfirmPasswordInput = (e) => {
    const inputValue = e.target.value;
    setConfirmPassword(inputValue);
    setHasConfirmPasswordInput(inputValue.length > 0);
  };

  const togglePasswordView = () => {
    setPasswordView(!passwordView);
  };

  const toggleConfirmPasswordView = () => {
    setConfirmPasswordView(!confirmPasswordView);
  };

  return (
    <CoverLayout image={bgImage}>
      <Card>
        <MKBox
          variant="gradient"
          bgColor="dark"
          borderRadius="lg"
          coloredShadow="success"
          mx={2}
          mt={-3}
          p={3}
          mb={1}
          textAlign="center"
        >
          <MKTypography variant="h4" fontWeight="medium" color="white" mt={1}>
            Reset Password
          </MKTypography>
          <MKTypography display="block" variant="button" color="white" my={1}>
            Create a new password for your account
          </MKTypography>
        </MKBox>
        <MKBox p={3}>
          {apiError && (
            <div
              className="bg-red-50 border border-red-400 text-red-500 px-4 py-2 mx-0 rounded-lg text-sm relative mb-4"
              role="alert"
            >
              <span className="block sm:inline">{apiError}</span>
            </div>
          )}

          {verifying ? (
            <MKBox textAlign="center" py={4}>
              <CircularProgress />
              <MKTypography variant="body2" color="text" mt={2}>
                Verifying your reset link...
              </MKTypography>
            </MKBox>
          ) : success ? (
            <MKBox textAlign="center" py={2}>
              <CheckCircleOutlineIcon color="success" style={{ fontSize: 60 }} />
              <MKTypography variant="h5" mt={2}>
                Password Reset Successful
              </MKTypography>
              <MKTypography variant="body2" color="text" mb={2}>
                Your password has been reset successfully.
              </MKTypography>
              <MKTypography variant="body2" color="text">
                You will be redirected to the login page in a few seconds...
              </MKTypography>
              <MKBox mt={4}>
                <MKButton
                  component={Link}
                  to="/sign-in/"
                  variant="gradient"
                  color="dark"
                >
                  Go to Login
                </MKButton>
              </MKBox>
            </MKBox>
          ) : !keyValid ? (
            <MKBox textAlign="center" py={2}>
              <ErrorOutlineIcon color="error" style={{ fontSize: 60 }} />
              <MKTypography variant="h5" mt={2}>
                Invalid Reset Link
              </MKTypography>
              <MKTypography variant="body2" color="text" mb={2}>
                This password reset link is invalid or has expired.
              </MKTypography>
              <MKBox mt={4}>
                <MKButton
                  component={Link}
                  to="/forgot-password/"
                  variant="gradient"
                  color="dark"
                >
                  Request New Reset Link
                </MKButton>
              </MKBox>
            </MKBox>
          ) : (
            <MKBox component="form" role="form" onSubmit={handleSubmit}>
              <MKBox mb={2}>
                <MKInput
                  type={passwordView ? "text" : "password"}
                  variant="standard"
                  label="New Password"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="start">
                        {hasPasswordInput && (
                          <Icon sx={{ cursor: "pointer" }} onClick={togglePasswordView} color="inherit">
                            {passwordView ? "visibility_off" : "visibility"}
                          </Icon>
                        )}
                      </InputAdornment>
                    ),
                  }}
                  fullWidth
                  onChange={handlePasswordInput}
                  error={!!errors.password}
                  helperText={errors.password}
                  FormHelperTextProps={{
                    sx: { color: "red" },
                  }}
                />
              </MKBox>
              <MKBox mb={2}>
                <MKInput
                  type={confirmPasswordView ? "text" : "password"}
                  variant="standard"
                  label="Confirm Password"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="start">
                        {hasConfirmPasswordInput && (
                          <Icon sx={{ cursor: "pointer" }} onClick={toggleConfirmPasswordView} color="inherit">
                            {confirmPasswordView ? "visibility_off" : "visibility"}
                          </Icon>
                        )}
                      </InputAdornment>
                    ),
                  }}
                  fullWidth
                  onChange={handleConfirmPasswordInput}
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword}
                  FormHelperTextProps={{
                    sx: { color: "red" },
                  }}
                />
              </MKBox>
              <MKBox mt={3} mb={1}>
                <MKButton
                  type="submit"
                  variant="gradient"
                  color="dark"
                  fullWidth
                  disabled={loading}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    "Reset Password"
                  )}
                </MKButton>
              </MKBox>
              <MKBox mt={3} mb={1} textAlign="center">
                <MKTypography variant="button" color="text">
                  Remember your password?{" "}
                  <MKTypography
                    component={Link}
                    to="/sign-in/"
                    variant="button"
                    color="info"
                    fontWeight="medium"
                    textGradient
                  >
                    Sign In
                  </MKTypography>
                </MKTypography>
              </MKBox>
            </MKBox>
          )}
        </MKBox>
      </Card>
    </CoverLayout>
  );
}

export default ResetPassword;
